import { useCallback, useMemo, useState } from 'react';
import { MixtureDraftSchema } from '../schemas/mixtures.schema';
import type {
	MaintenanceMixture,
	MaintenanceMixtureDraft,
	MaintenanceMixtureProduct,
} from '../types';

type ProductType = MaintenanceMixtureProduct['type'];
type ProductUnit = MaintenanceMixtureProduct['unit'];

export interface MixtureComposerRow {
	key: string;
	productKey: string;
	dose: string;
	locked?: { dose: number; unit: ProductUnit };
}

const BASE_UNIT: Record<ProductType, ProductUnit> = {
	fertilizer: 'kg',
	defensive: 'L',
};

const TO_BASE_UNIT: Record<ProductUnit, number> = {
	kg: 1,
	g: 0.001,
	L: 1,
	mL: 0.001,
};

export const toMixtureProductKey = (type: ProductType, id: string) => `${type}:${id}`;

const parseMixtureProductKey = (productKey: string) => {
	const [type, id] = productKey.split(':');
	return { type: type as ProductType, id };
};

export const mixtureUnitFor = (productKey: string) =>
	productKey ? BASE_UNIT[parseMixtureProductKey(productKey).type] : '';

let rowSequence = 0;
const newRow = (
	row: Omit<MixtureComposerRow, 'key'> = { productKey: '', dose: '' }
): MixtureComposerRow => {
	rowSequence += 1;
	return { key: `row-${rowSequence}`, ...row };
};

const toDraftProduct = (row: MixtureComposerRow) => {
	const { type, id } = parseMixtureProductKey(row.productKey);
	return row.locked
		? { id, type, dose: row.locked.dose, unit: row.locked.unit }
		: { id, type, dose: Number(row.dose), unit: BASE_UNIT[type] };
};

export const useMixtureComposer = () => {
	const [editingId, setEditingId] = useState<string | null>(null);
	const [rows, setRows] = useState<MixtureComposerRow[]>([]);
	const [date, setDateValue] = useState<string | null>(null);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const clearError = (key: string) => setErrors((current) => ({ ...current, [key]: '' }));

	const addProduct = () => {
		setRows((current) => [...current, newRow()]);
		setDateValue((current) => current ?? '');
	};

	const removeProduct = (key: string) =>
		setRows((current) => current.filter((row) => row.key !== key));

	const updateRow = (key: string, field: 'productKey' | 'dose', value: string) => {
		setRows((current) =>
			current.map((row) => (row.key === key && !row.locked ? { ...row, [field]: value } : row))
		);
		clearError(`${field}-${key}`);
		clearError('products');
	};

	const setDate = (value: string) => {
		setDateValue(value);
		clearError('date');
	};

	const reset = useCallback(() => {
		setEditingId(null);
		setRows([]);
		setDateValue(null);
		setErrors({});
	}, []);

	const load = (mixture: MaintenanceMixture) => {
		setEditingId(mixture._id);
		setRows(
			mixture.products.map((product) =>
				newRow({
					productKey: toMixtureProductKey(product.type, product.id),
					dose: String(product.dose * TO_BASE_UNIT[product.unit]),
					locked: product.isFromCombo ? { dose: product.dose, unit: product.unit } : undefined,
				})
			)
		);
		setDateValue(mixture.date.slice(0, 10));
		setErrors({});
	};

	const products = useMemo(
		() =>
			rows
				.filter((row) => row.productKey && Number(row.dose) > 0)
				.map((row) => {
					const { id, type } = parseMixtureProductKey(row.productKey);
					return { id, type, dose: Number(row.dose), unit: BASE_UNIT[type] };
				}),
		[rows]
	);

	const buildDraft = (): MaintenanceMixtureDraft | null => {
		const parsed = MixtureDraftSchema.safeParse({ date: date ?? '', products: rows });
		if (!parsed.success) {
			const nextErrors: Record<string, string> = {};
			parsed.error.issues.forEach((issue) => {
				const [field, index, key] = issue.path;
				if (field === 'products' && typeof index === 'number') {
					nextErrors[`${String(key)}-${rows[index]?.key}`] = issue.message;
				} else {
					nextErrors[String(field)] = issue.message;
				}
			});
			setErrors(nextErrors);
			return null;
		}
		return { date: parsed.data.date, products: rows.map(toDraftProduct) };
	};

	return {
		editingId,
		rows,
		date,
		errors,
		products,
		isEmpty: rows.length === 0,
		addProduct,
		removeProduct,
		updateRow,
		setDate,
		load,
		reset,
		buildDraft,
	};
};
